import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { PHASES } from "./PhaseTracker";
import { Lightbulb } from "lucide-react";
import { getContextualHelpPrompts } from "../lib/help";

/**
 * HelpModal — methodology-aware live prompts contextual to phase + captured
 * state. Bullet-point only, no scripts, no walls of text. V0.3.11 replaces
 * the previous static dict with getContextualHelpPrompts() so prompts react
 * to what's MISSING or UNDERDEVELOPED in the live conversation.
 */
export default function HelpModal({ open, onOpenChange, phase, state, transcript, mode = "b2b" }) {
  const meta = PHASES.find((p) => p.id === phase) || PHASES[0];
  const content = getContextualHelpPrompts({ phase, state, transcript, mode });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="help-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto sch-scroll p-0 gap-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 mb-1.5">
            <Lightbulb className="h-3.5 w-3.5" />
            Prompts for {meta.full}
            <span
              data-testid="help-mode-badge"
              className="ml-1 rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9px] font-mono"
            >
              {mode.toUpperCase()}
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight text-slate-900">
            Need a nudge?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            {content.sub}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {content.sections.length === 0 && (
            <p className="text-sm text-slate-500 italic">Nothing to nudge you on yet — keep listening.</p>
          )}
          {content.sections.map((s, i) => (
            <section key={i} data-testid={`help-section-${i}`}>
              <h4 className="text-[11px] uppercase tracking-widest text-slate-500 font-display font-semibold mb-2">
                {s.title}
              </h4>
              <ul className="space-y-1.5">
                {s.items.map((item, j) => (
                  <li
                    key={j}
                    className="flex gap-2.5 text-sm text-slate-800 leading-relaxed"
                  >
                    <span className="text-amber-600 mt-1.5 shrink-0">▸</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 text-xs text-slate-500">
          Prompts adapt to what's missing in the call. Switch phases on the tracker to see different prompts.
        </footer>
      </DialogContent>
    </Dialog>
  );
}
